import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import {
  MONEY_SCALE,
  compare,
  formatMoney,
  fromString,
  isPositive,
  parseHumanAmount,
  toString,
} from '@aeris/money'
import { Field, InlineAlert, Input, RadioGroup } from '@aeris/ui'

import { adjustCustomerCredit } from '../lib/api/exchanges.ts'
import { ApiError } from '../lib/api.ts'

import { AuthorisationDialog } from './authorisation-dialog.tsx'

/**
 * Dar ou tirar vale na mão — F216.
 *
 * O dono: "o admin quer conseguir editar os vales, dar vale, subtrair vale, na
 * mão, com a senha dele". Um diálogo só: o valor e o lado (loja ou gestão
 * antiga) ficam à vista de quem digita a senha, e vão junto dela — o servidor
 * só grava se o ajuste for exatamente o aprovado.
 *
 * O lado importa porque a gestão antiga ressarce à Elite o vale antigo que o
 * cliente gasta. "Esse cliente tinha crédito com o dono antigo" entra do lado
 * antigo; tirar um vale dado errado sai do lado em que ele entrou.
 */

type Side = 'store' | 'old'

const money = (wire: string): string =>
  formatMoney(fromString(wire, MONEY_SCALE))

export const CreditAdjustmentDialog = ({
  partyId,
  customerName,
  direction,
  open: available,
  onClose,
}: {
  partyId: string
  customerName: string
  direction: 'give' | 'take'
  /** O que está em aberto de cada lado — tirar não passa disso. */
  open: { store: string; old: string }
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [amountText, setAmountText] = useState('')
  const [side, setSide] = useState<Side | null>(null)
  const [error, setError] = useState<string | null>(null)

  const amount = parseHumanAmount(amountText, MONEY_SCALE)
  const wire = amount === null ? null : toString(amount)
  const limit =
    side === null ? null : side === 'old' ? available.old : available.store
  const overLimit =
    direction === 'take' &&
    amount !== null &&
    limit !== null &&
    compare(amount, fromString(limit, MONEY_SCALE)) > 0
  const ready =
    amount !== null && isPositive(amount) && side !== null && !overLimit

  const adjust = useMutation({
    mutationFn: async (input: { authorisationId: string; reason: string }) => {
      if (wire === null || side === null) throw new Error('ajuste incompleto')
      return await adjustCustomerCredit(partyId, {
        direction,
        amount: wire,
        oldManagement: side === 'old',
        reason: input.reason,
        authorisationId: input.authorisationId,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['customer-credit', partyId],
      })
      onClose()
    },
    onError: (failure: unknown) => {
      setError(
        failure instanceof ApiError
          ? failure.message
          : 'Não foi possível gravar o ajuste. Tente de novo.'
      )
    },
  })

  const give = direction === 'give'

  return (
    <AuthorisationDialog
      title={give ? 'Dar vale' : 'Tirar vale'}
      description={`${customerName}. Só o dono ou o admin, com a senha.`}
      action="exchange.credit.adjust"
      targetType="party"
      targetId={partyId}
      detail={
        wire === null || side === null
          ? undefined
          : { direction, amount: wire, oldManagement: side === 'old' }
      }
      canAuthorise={ready && !adjust.isPending}
      summary={
        <div className="flex flex-col gap-3">
          <Field label="Valor">
            {(field) => (
              <Input
                id={field.id}
                aria-describedby={field['aria-describedby']}
                inputMode="decimal"
                autoComplete="off"
                placeholder="0,00"
                value={amountText}
                onChange={(event) => {
                  setAmountText(event.target.value)
                  setError(null)
                }}
              />
            )}
          </Field>
          <Field label="De quem é o vale">
            {(field) => (
              <RadioGroup<Side>
                id={field.id}
                aria-describedby={field['aria-describedby']}
                name="credit-adjustment-side"
                value={side}
                onValueChange={(value) => {
                  setSide(value)
                  setError(null)
                }}
                options={[
                  {
                    value: 'store',
                    label: 'Da loja',
                    description: give
                      ? 'A Elite deve ao cliente.'
                      : `Em aberto: ${money(available.store)}`,
                  },
                  {
                    value: 'old',
                    label: 'Da gestão antiga',
                    description: give
                      ? 'Crédito que o cliente tinha com o dono antigo — entra na conta a cobrar.'
                      : `Em aberto: ${money(available.old)}`,
                  },
                ]}
              />
            )}
          </Field>
          {overLimit ? (
            <InlineAlert tone="warning">
              {`Esse lado só tem ${money(limit)} em aberto.`}
            </InlineAlert>
          ) : null}
          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}
        </div>
      }
      onAuthorised={(authorisationId, reason) => {
        adjust.mutate({ authorisationId, reason })
      }}
      onClose={onClose}
    />
  )
}
